// src/layouts/HorizontalLayout.jsx

import React, { Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box,
    Container,
    CircularProgress,
} from '@mui/material';
import ArcherChat from '../components/ArcherChat';

// Lazy load main sections
const Navbar = React.lazy(() => import('../components/Navbar'));

// Navbar's own Toolbar uses MUI's responsive minHeight (56px below `sm`,
// 64px at `sm`+) - this offset has to match it or there's an 8px gap/overlap
// below 600px.
const NAVBAR_HEIGHT = { xs: '56px', sm: '64px' };

const loading = (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100dvh',
        }}
    >
        {/* Header placeholder */}
        <Box component="header" sx={{ height: NAVBAR_HEIGHT }} />
        {/* Content placeholder */}
        <Box component="main" sx={{ flex: 1 }} />
    </Box>
);

const HorizontalLayout = ({ children }) => {
    const location = useLocation();
    const pageContext = { url: location.pathname, title: document.title };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                // 100dvh (dynamic viewport height) excludes mobile browser
                // chrome; 100vh is the fallback for browsers without dvh support.
                minHeight: '100vh',
                '@supports (height: 100dvh)': { minHeight: '100dvh' },
                width: '100%',
                overflowX: 'hidden', // Prevent horizontal scroll
            }}
        >
            {/* Fixed Navbar (horizontal) */}
            <Box
                sx={{
                    position: 'fixed',
                    width: '100%',
                    zIndex: 1200,
                    height: NAVBAR_HEIGHT,
                    boxShadow: theme => theme.shadows[2],
                    backgroundColor: theme => theme.palette.background.paper,
                }}
            >
                <Suspense fallback={loading}>
                    <Navbar />
                </Suspense>
            </Box>

            {/* Main content, scrollable below navbar */}
            <Box
                component="main"
                sx={{
                    flex: 1,
                    mt: NAVBAR_HEIGHT, // margin-top to offset fixed navbar
                    height: {
                        xs: `calc(100vh - ${NAVBAR_HEIGHT.xs})`,
                        sm: `calc(100vh - ${NAVBAR_HEIGHT.sm})`,
                    },
                    // dvh variants override the vh fallback above where supported
                    '@supports (height: 100dvh)': {
                        height: {
                            xs: `calc(100dvh - ${NAVBAR_HEIGHT.xs})`,
                            sm: `calc(100dvh - ${NAVBAR_HEIGHT.sm})`,
                        },
                    },
                    overflowY: 'auto', // Only vertical scroll
                    overflowX: 'hidden', // Prevent horizontal scroll
                    width: '100%',
                    position: 'relative',
                    backgroundColor: theme => theme.palette.background.default,
                }}
            >
                <Container
                    maxWidth={false}
                    disableGutters
                    sx={{
                        px: { xs: 2, sm: 3 },
                        // Bottom clearance so page content can scroll clear of
                        // the ArcherChat FAB (fixed bottom-right, ~80x80px).
                        // ArcherChat itself is out of scope for this pass.
                        pb: { xs: 10, sm: 4 },
                    }}
                >
                    <Suspense
                        fallback={
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <CircularProgress />
                            </Box>
                        }>
                        {children}
                    </Suspense>
                </Container>
            </Box>

            <ArcherChat pageContext={pageContext} />
        </Box>
    );
};

export default HorizontalLayout;
